import { ErrorHandler, inject, Injectable, NgZone } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import * as Sentry from "@sentry/angular-ivy";
import { DialogsService } from "../../../shared/services/dialogs/dialogs.service";
import { ConfirmationDialogComponent } from "../../../shared/components/confirmation-dialog/confirmation-dialog.component";
import { NetworkStatusService } from "../../../shared/services/network-status.service";
import { SnackService } from "../../../shared/services/snack-service.service";
import { TranslateUtils } from "../../../shared/utils/translate.utils";

@Injectable()
export class GlobalErrorHandler extends ErrorHandler {
  private zone = inject(NgZone);
  private dialogsService = inject(DialogsService);
  private network = inject(NetworkStatusService);
  private snack = inject(SnackService);

  private alreadyPrompted = false;

  constructor() {
    super();
  }

  handleError(error: any): void {
    if (error.name === "EmptyError") {
      return;
    }
    if (this.isChunkLoadError(error)) {
      // PAS DE RÉSEAU (01/10/2026) : le code d'un écran / d'un dialog a pu manquer parce que le réseau
      // est coupé, pas parce qu'une version est sortie — et « Rafraîchir » donnerait alors la page
      // d'erreur du navigateur (l'application entière perdue). On VÉRIFIE d'abord (sonde du serveur) :
      // joignable → c'est une nouvelle version, on propose de rafraîchir ; injoignable → on le dit.
      void this.network.probeNow().then((joignable) => {
        if (joignable) this.promptReload();
        else this.zone.run(() => this.snack.error(TranslateUtils.translate("system.network.chunk_offline")));
      });
    }

    super.handleError(error);

    // Ne pas envoyer les erreurs HTTP à Sentry — gérées par l'interceptor
    if (error instanceof HttpErrorResponse) return;
    const inner = error?.rejection ?? error;
    if (inner?.status && inner?.headers) return;

    Sentry.captureException(error);
  }

  /** Une nouvelle version est en ligne : rafraîchir (une fois ; la seconde, retour à l'accueil). */
  private promptReload(): void {
    if (this.alreadyPrompted) return;
    this.zone.run(() => {
      this.alreadyPrompted = true;
      const ref = this.dialogsService.open(
        ConfirmationDialogComponent,
        "fit-content",
        "fit-content",
        {
          title: "Mise à jour de l'application",
          message:
            "Une nouvelle version de l'application est en ligne. Merci de bien vouloir rafraîchir la page.",
        },
      );
      ref.afterClosed().subscribe((v) => {
        this.alreadyPrompted = false;
        if (!v) return;
        const key = "chunk_reload_attempted";
        if (!sessionStorage.getItem(key)) {
          sessionStorage.setItem(key, "1");
          location.reload();
        } else {
          location.href = "/";
        }
      });
    });
  }

  isChunkLoadError(err: any): boolean {
    const msg = (err?.message || err?.toString?.() || "").toLowerCase();
    return (
      msg.includes("loading chunk") ||
      msg.includes("chunkloaderror") ||
      (msg.includes("import()") && msg.includes("failed"))
    );
  }
}
